import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { buttonText } from "@/lib/buttonTexts";
import EkkoLogo from "@/components/EkkoLogo";

export default function Header({ content = {}, navItems = [] }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Por cima da foto do topo o cabeçalho é transparente: texto e logótipo a branco
  const onDark = !isScrolled && !isMobileMenuOpen;

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass py-3" : "py-5 bg-transparent"
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a
              href="#"
              className="flex items-center gap-3"
              data-testid="logo"
            >
              <EkkoLogo content={content} className="ekko-logo h-8 md:h-9 w-auto text-[#A6C48A]" />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors ${onDark ? "text-white/85 hover:text-white" : "text-[#C9C9C9] hover:text-[#FFFFFF]"}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection("#contacto")}
                className={`btn-skew px-6 py-2 font-bold text-sm uppercase tracking-wider bg-[#A6C48A] text-[#010000] hover:bg-[#B9D3A1]`}
                data-testid="nav-cta"
              >
                <span>{buttonText(content, "header_cta")}</span>
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${onDark ? "text-white" : "text-[#F5F5F5]"}`}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#010000] pt-20 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col items-center gap-6 p-8">
              {navItems.map((link, index) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(link.href)}
                  className="text-2xl font-medium text-[#F5F5F5] uppercase tracking-widest"
                  
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navItems.length * 0.1 }}
                onClick={() => scrollToSection("#contacto")}
                className="btn-skew bg-[#A6C48A] text-[#010000] px-8 py-3 font-bold text-lg uppercase tracking-wider hover:bg-[#B9D3A1] mt-4"
                data-testid="mobile-nav-cta"
              >
                <span>{buttonText(content, "header_cta")}</span>
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
